import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/auth";
import { ApiError } from "@/lib/api";
import { deleteAccount } from "@/lib/notifications-api";

/**
 * apps/web :: components :: DangerZone
 *
 * Red-bordered card at the bottom of `/settings` that exposes GDPR Art. 17
 * "right to erasure" — a single-action, two-step-confirmed full account
 * deletion. Fires `DELETE /api/gdpr/me` which cascades deletes across the
 * notification preferences, push subscriptions, telegram binding, active
 * sessions (memory + Redis), and the upstream WorkOS user record.
 *
 * UX contract:
 *   1. Primary button — "Delete my account". Click opens the inline
 *      alert dialog.
 *   2. Dialog asks "Are you sure?" AND requires the user to type the
 *      literal word "DELETE" before the final destroy button enables.
 *      This defends against:
 *        - Accidental double-click on the primary button.
 *        - Passwordless confirmation (the user has to prove intent with
 *          an exact string, not just a click).
 *   3. On confirm: calls `deleteAccount()`. On 2xx, flushes local auth
 *      state via `useAuth().logout()` (best-effort; server already cleared
 *      the cookie) and navigates to `/login` with a success flash.
 *   4. On failure: inline error kept visible; dialog stays open so the
 *      user can retry without re-typing "DELETE".
 *
 * Accessibility:
 *   - The dialog is rendered inline (not a modal) so screen readers pick
 *     it up as part of the flow without focus-trap complexity.
 *   - `role="alertdialog"` + linked `aria-labelledby` / `aria-describedby`
 *     match the pattern established in NotificationPreferences' delete
 *     confirmation.
 */
const CONFIRMATION_PHRASE = "DELETE";

interface DeleteState {
  kind: "idle" | "deleting" | "error";
  errorMessage?: string;
}

export default function DangerZone() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [typed, setTyped] = useState<string>("");
  const [state, setState] = useState<DeleteState>({ kind: "idle" });

  const canConfirm = typed.trim() === CONFIRMATION_PHRASE;

  async function handleDelete(): Promise<void> {
    if (!canConfirm) return;
    setState({ kind: "deleting" });
    try {
      await deleteAccount();
      // Best-effort local logout — the server already cleared the cookie
      // and invalidated every session. If logout()'s HTTP call fails (it
      // will, the session no longer exists), the AuthContext still flips
      // to anonymous in its finally block, which is what we want.
      await logout();
      // Use replace so the back button doesn't return the now-erased user
      // to /settings where the auth probe would 401 and bounce them anyway.
      navigate("/login?deleted=1", { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setState({ kind: "error", errorMessage: message });
    }
  }

  return (
    <section
      aria-labelledby="danger-zone-heading"
      className="mt-xl rounded-lg border-2 border-danger/40 bg-danger/5 p-lg sm:p-xl"
      data-testid="danger-zone"
    >
      <header>
        <h2
          id="danger-zone-heading"
          className="font-display text-xl font-bold text-danger"
        >
          {t("settings.dangerZone.title")}
        </h2>
        <p className="mt-xs text-sm text-neutral-800">
          {t("settings.dangerZone.body")}
        </p>
      </header>

      <div className="mt-lg">
        {!confirmOpen ? (
          <button
            type="button"
            onClick={() => {
              setConfirmOpen(true);
              setTyped("");
              setState({ kind: "idle" });
            }}
            data-testid="danger-delete-account-button"
            className="inline-flex items-center justify-center rounded-md border border-danger bg-white px-md py-sm text-sm font-semibold text-danger transition-colors hover:bg-danger hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
          >
            {t("settings.dangerZone.cta")}
          </button>
        ) : (
          <div
            role="alertdialog"
            aria-labelledby="danger-confirm-title"
            aria-describedby="danger-confirm-body"
            data-testid="danger-delete-confirm"
            className="flex flex-col gap-sm rounded-md border border-danger bg-white p-md text-sm"
          >
            <p id="danger-confirm-title" className="font-semibold text-danger">
              {t("settings.dangerZone.confirmTitle")}
            </p>
            <p id="danger-confirm-body" className="text-neutral-800">
              {t("settings.dangerZone.confirmBody")}
            </p>
            <label className="flex flex-col gap-xs">
              <span className="text-xs font-medium text-neutral-700">
                {t("settings.dangerZone.typeToConfirmLabel", {
                  phrase: CONFIRMATION_PHRASE,
                })}
              </span>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid="danger-confirm-input"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                className="rounded-md border border-neutral-300 bg-white px-sm py-xs font-mono text-sm text-neutral-900 focus:border-danger focus:outline-none focus:ring-1 focus:ring-danger"
              />
            </label>
            <div className="mt-xs flex gap-sm">
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={!canConfirm || state.kind === "deleting"}
                data-testid="danger-delete-confirm-button"
                className="inline-flex items-center justify-center rounded-md bg-danger px-md py-xs text-xs font-semibold text-white hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:cursor-not-allowed disabled:opacity-50"
              >
                {state.kind === "deleting"
                  ? t("settings.dangerZone.deleting")
                  : t("settings.dangerZone.confirm")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmOpen(false);
                  setTyped("");
                  setState({ kind: "idle" });
                }}
                data-testid="danger-delete-cancel-button"
                className="inline-flex items-center justify-center rounded-md border border-neutral-300 bg-white px-md py-xs text-xs font-medium text-neutral-800 hover:bg-neutral-50"
              >
                {t("settings.dangerZone.cancel")}
              </button>
            </div>
            {state.kind === "error" && (
              <p
                role="alert"
                data-testid="danger-delete-error"
                className="text-xs font-medium text-danger"
              >
                {t("settings.dangerZone.error", { message: state.errorMessage })}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
